import { useCallback, useMemo } from 'react';
import { useQuery, useMutation } from '@apollo/client/react';
import {
  LIST_MEDIA,
  GET_MEDIA,
  CREATE_PRESIGNED_UPLOAD,
  DELETE_MEDIA,
} from '../graphql/operations';
import useApi from './useApi';

// Hook for listing media with Apollo
export function useListMedia(variables = {}) {
  const { data, loading, error, fetchMore } = useQuery(LIST_MEDIA, {
    variables: { input: variables },
  });

  return {
    items: data?.listMedia?.items || [],
    loading,
    error,
    nextToken: data?.listMedia?.nextToken,
    fetchMore: () => {
      if (data?.listMedia?.nextToken) {
        fetchMore({
          variables: {
            input: {
              ...variables,
              nextToken: data.listMedia.nextToken,
            },
          },
        });
      }
    },
  };
}

// Hook for getting single media item with Apollo
export function useGetMedia(mediaId) {
  const { data, loading, error } = useQuery(GET_MEDIA, {
    variables: { mediaId },
    skip: !mediaId,
  });

  return {
    item: data?.getMedia || null,
    loading,
    error,
  };
}

// Hook for creating presigned upload URL
export function useCreatePresignedUpload() {
  const [createUpload, { loading, error }] = useMutation(CREATE_PRESIGNED_UPLOAD, {
    refetchQueries: [{ query: LIST_MEDIA, variables: { input: {} } }],
  });

  const execute = async (file) => {
    const { data } = await createUpload({
      variables: {
        input: {
          filename: file.name,
          contentType: file.type,
        },
      },
    });
    return data.createPresignedUpload;
  };

  return { execute, loading, error };
}

// Hook for deleting media
export function useDeleteMedia() {
  const [deleteMedia, { loading, error }] = useMutation(DELETE_MEDIA, {
    refetchQueries: [{ query: LIST_MEDIA, variables: { input: {} } }],
  });

  const execute = async (mediaId) => {
    const { data } = await deleteMedia({
      variables: { mediaId },
    });
    return data.deleteMedia;
  };

  return { execute, loading, error };
}

// Legacy default export with REST API fallback for backward compatibility
export default function useMedia() {
  const api = useApi();

  const listMedia = useCallback(
    ({ type, limit, nextToken } = {}) => {
      const params = new URLSearchParams();
      if (type) params.set('type', type);
      if (limit) params.set('limit', limit);
      if (nextToken) params.set('nextToken', nextToken);
      const query = params.toString();
      return api.get(`/media${query ? `?${query}` : ''}`);
    },
    [api],
  );

  const createUpload = useCallback((payload) => api.post('/media/presigned', payload), [api]);
  const getMediaItem = useCallback((mediaId) => api.get(`/media/${mediaId}`), [api]);
  const deleteMedia = useCallback((mediaId) => api.del(`/media/${mediaId}`), [api]);

  return useMemo(
    () => ({
      listMedia,
      createUpload,
      getMediaItem,
      deleteMedia,
    }),
    [listMedia, createUpload, getMediaItem, deleteMedia],
  );
}
